import type { NFTCollateralPosition } from "@stepfinance/lending";
import type { StepColumnType } from "@stepfinance/react";
import {
  buildImageURL,
  CellDefault,
  CheckboxFilter,
  formatPercentage,
  makeSimpleFilters,
  StepPopoverCellWrapper,
  TagInfo,
  TagInfoContainer,
  URLIcon,
} from "@stepfinance/react";
import Countdown from "react-countdown";

import type { ColumnMapType, ExtendedStepColumnsType, GetColumnsArgs } from "../../../../utils/columns";
import { countdownRenderString } from "../../../../utils/utils";
import { IconTextContainer, TokenIconText } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { NFTLendingPopover } from "../NFTLendingPopover";
import { LendingBalanceCell, NetPositionCell } from "./shared";

const assetColumn = ({ commonText }: GetColumnsArgs["args"]): StepColumnType<NFTCollateralPosition> => ({
  title: commonText.asset,
  dataIndex: "asset",
  key: `asset-column`,
  render(_, { name, imageUrl }) {
    return (
      <>
        {name ? (
          <IconTextContainer>
            <URLIcon src={buildImageURL(imageUrl ?? "", "32")} />
            <TokenIconText bold>{name}</TokenIconText>
          </IconTextContainer>
        ) : (
          <CellDefault />
        )}
      </>
    );
  },
  sorter: (a, b) => {
    const collectionA = a.name || "";
    const collectionB = b.name || "";

    return collectionA.localeCompare(collectionB);
  },
});

const collectionColumn = ({
  collections,
  nftGallery,
}: GetColumnsArgs["args"]): StepColumnType<NFTCollateralPosition> => ({
  title: nftGallery.nftCollection,
  dataIndex: "collection",
  key: "collection",
  className: "column-string",
  filters: makeSimpleFilters(collections),
  filterDropdown: (props) => (
    <CheckboxFilter
      searchable
      searchPlaceholder={nftGallery.searchCollection}
      emptySearchResultsPrompt={nftGallery.noCollectionFound}
      {...props}
    />
  ),
  onFilter: (value, record) => record.collection.name!.startsWith(value.toString()),
  render(_, record) {
    return <CellValue cellType="text">{record.collection.name}</CellValue>;
  },
  sorter: (a, b) => {
    const collectionA = a.collection.name || "";
    const collectionB = b.collection.name || "";

    return collectionA.localeCompare(collectionB);
  },
});

const loanColumn = ({ commonText, lendingText }: GetColumnsArgs["args"]): StepColumnType<NFTCollateralPosition> => ({
  title: lendingText.loan,
  dataIndex: "loanAmount",
  key: `loan-column`,
  className: "column-number",
  filters: [
    { text: lendingText.nftLending.loanStatus.offered, value: "offered" },
    { text: lendingText.nftLending.loanStatus.borrowed, value: "borrowed" },
    { text: lendingText.nftLending.loanStatus.lending, value: "lending" },
  ],
  filterDropdown: (props) => <CheckboxFilter {...props} />,
  onFilter: (value, record) => record.status.startsWith(value as string),
  render(_, record) {
    return (
      <LendingBalanceCell>
        <StepPopoverCellWrapper>
          <div className="asset-value">
            <CellValue cellType="number">{record.loanAmount.toFixed(2)}</CellValue>
          </div>
          <NFTLendingPopover record={record} common={commonText} nftLending={lendingText.nftLending} />
        </StepPopoverCellWrapper>
      </LendingBalanceCell>
    );
  },
  sorter: (a, b) => {
    return a.loanAmount - b.loanAmount;
  },
});

const timeRemainingColumn = ({
  singleAssetStaking,
}: GetColumnsArgs["args"]): StepColumnType<NFTCollateralPosition> => ({
  title: singleAssetStaking.timeRemaining,
  dataIndex: "loanEndTs",
  key: "time-column",
  className: "column-number",
  render(_, record) {
    return (
      <>
        {record.loanEndTs ? (
          <Countdown
            date={record.loanEndTs * 1000}
            renderer={(args) => <CellValue cellType="number">{countdownRenderString(args)}</CellValue>}
          ></Countdown>
        ) : (
          <CellDefault />
        )}
      </>
    );
  },
  sorter: (a, b) => {
    // default to infinity for offered loans, so shortest time remaining is at top when ascending
    return (a.loanEndTs ?? Infinity) - (b.loanEndTs ?? Infinity);
  },
  defaultSortOrder: "ascend",
});

const netColumn = ({ lendingText }: GetColumnsArgs["args"]): StepColumnType<NFTCollateralPosition> => ({
  title: lendingText.netPositionValue,
  dataIndex: "net",
  key: `net-column`,
  className: "column-number",
  render(_, record) {
    return (
      <NetPositionCell>
        <CellValue cellType="userPositionValue" fontWeight="bold">
          {record.net}
        </CellValue>
        <TagInfoContainer>
          <TagInfo performanceNumber={record.apy ?? 0} tagType="performance">
            {formatPercentage(record.apy ?? 0, { performance: true })}
          </TagInfo>
        </TagInfoContainer>
      </NetPositionCell>
    );
  },
  sorter: (a, b) => {
    return (a.net ?? 0) - (b.net ?? 0);
  },
});

const defaultColumns: ExtendedStepColumnsType<NFTCollateralPosition> = [
  assetColumn,
  collectionColumn,
  loanColumn,
  timeRemainingColumn,
  netColumn,
];

export const nftLendingColumnMap: ColumnMapType<NFTCollateralPosition> = {
  default: defaultColumns,
};
